import React, { memo, useMemo, useState } from 'react';
import { TopPicks4U } from '@browsing/top-picks-4U';
import { BiggestDiscountCarousel } from '@browsing/biggest-discount';
import { MIN_CAMPAIGNS_TO_DISPLAY_BUTTON, TOTAL_CAMPAIGNS_TO_DISPLAY } from '@browsing/config';
import { trackSelectContent } from '@browsing/tracking';
import { ShowMoreCampaignsSection } from '../../../campaign-volume';
import { NewDealsCarousel } from '../../../new-deals-today';
import { SemanticArticleCarousel } from '../../../semantic-article-carousel';
import { ContinueShoppingCarousel } from '../../../../continue-shopping';
import { useGridLayoutContext } from '../context/GridLayoutContext';
import { CampaignBlock, Slot, DeclarativeGrid } from '../components';

/**
 * OneColumnLayout - Mobile layout for the campaign grid (1 column).
 *
 * IMPORTANT: All non-CampaignBlock components (carousels, widgets, etc.) MUST be wrapped
 * with ShowstopperBlock to prevent position changes when components are conditionally hidden.
 *
 * Why ShowstopperBlock is Required:
 * - When a carousel is hidden (e.g., condition={false}), it renders a CampaignBlock as fallback
 * - This prevents other carousels from shifting positions
 * - Maintains consistent layout and campaign positioning
 *
 * Usage Pattern:
 *   ✅ CORRECT:
 *   <ShowstopperBlock condition={true} fallbackLayout={[Slot.MOBILE]}>
 *     <SemanticArticleCarousel />
 *   </ShowstopperBlock>
 *
 *   ❌ INCORRECT:
 *   <SemanticArticleCarousel />  // This will cause position shifts when hidden
 *
 * Exceptions (components that don't need ShowstopperBlock):
 *   - CampaignBlock: Already part of the grid system
 *   - ShowMoreCampaignsSection: Renders at the end, doesn't affect carousel positioning
 *
 * When Adding New Components:
 *   1. Wrap ALL carousels and widgets with ShowstopperBlock
 *   2. Use condition={true} for now (adjust conditions later as needed)
 *   3. Use fallbackLayout={[Slot.MOBILE]} for one-column layout
 *   4. Have code review verify ShowstopperBlock usage
 */
const OneColumnLayoutComponent: React.FC = () => {
    const {
        campaigns,
        isNewDealsToday,
        isSemanticArticleCarousel,
        topPicks4UGenderData,
        isContinueShopping,
    } = useGridLayoutContext();
    const { gender, profileFashionPreference, selectedFashionPreference } = topPicks4UGenderData;

    const [areCampaignsExpanded, setAreCampaignsExpanded] = useState(false);

    // Manage campaigns to show based on expansion state
    const [campaignsToShow, setCampaignsToShow] = useState(() =>
        campaigns.slice(0, TOTAL_CAMPAIGNS_TO_DISPLAY),
    );

    const handleExpandableButton = () => {
        trackSelectContent('see_all_button', 'click', {});
        setAreCampaignsExpanded(true);
        setCampaignsToShow(campaigns);
    };

    const openCampaignsCampaignVolumeCarousel = useMemo(() => {
        if (campaigns.length <= TOTAL_CAMPAIGNS_TO_DISPLAY) {
            return [];
        }

        return campaigns.slice(TOTAL_CAMPAIGNS_TO_DISPLAY, TOTAL_CAMPAIGNS_TO_DISPLAY + 5);
    }, [campaigns]);

    const MORE_CAMPAIGNS_COUNT = campaigns.length - TOTAL_CAMPAIGNS_TO_DISPLAY;
    const shouldRenderShowMoreCampaignsButton =
        !areCampaignsExpanded && campaigns.length >= MIN_CAMPAIGNS_TO_DISPLAY_BUTTON;

    return (
        <DeclarativeGrid
            fillRemainingSlot={Slot.MOBILE}
            customCampaignsList={campaignsToShow}
            afterFillRemaining={
                shouldRenderShowMoreCampaignsButton && (
                    <ShowMoreCampaignsSection
                        campaigns={openCampaignsCampaignVolumeCarousel}
                        moreCampaignsCount={MORE_CAMPAIGNS_COUNT}
                        onExpandClick={handleExpandableButton}
                    />
                )
            }
        >
            <CampaignBlock slots={[Slot.MOBILE]} />
            <CampaignBlock slots={[Slot.MOBILE]} />
            {isSemanticArticleCarousel ? (
                <SemanticArticleCarousel />
            ) : (
                <CampaignBlock slots={[Slot.MOBILE]} />
            )}
            <CampaignBlock slots={[Slot.MOBILE]} />
            <TopPicks4U
                isMobileScreen
                gender={gender}
                profileFashionPreference={profileFashionPreference}
                selectedFashionPreference={selectedFashionPreference}
            />
            <CampaignBlock slots={[Slot.MOBILE]} />
            {isNewDealsToday ? <NewDealsCarousel /> : <CampaignBlock slots={[Slot.MOBILE]} />}
            <CampaignBlock slots={[Slot.MOBILE]} />
            {isContinueShopping ? (
                <ContinueShoppingCarousel />
            ) : (
                <CampaignBlock slots={[Slot.MOBILE]} />
            )}
            <CampaignBlock slots={[Slot.MOBILE]} />
            <BiggestDiscountCarousel
                profileFashionPreference={profileFashionPreference}
                selectedFashionPreference={selectedFashionPreference}
            />
            <CampaignBlock slots={[Slot.MOBILE]} />
            {/*
                DeclarativeGrid's FillRemaining automatically renders all remaining campaigns.
                ShowMoreCampaignsSection is passed via afterFillRemaining prop to render after all campaigns.
            */}
        </DeclarativeGrid>
    );
};

export const OneColumnLayout = memo(OneColumnLayoutComponent);
